// ============================================================================
// PermissionCards – Airbnb-style selectable permission cards (Viewer, Worker, Manager, Admin)
// Maps to backend access_level: Admin => full, Viewer/Worker/Manager => limited.
// ============================================================================

import { useEffect } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export type PermissionLevel = "viewer" | "worker" | "manager" | "admin";

export const PERMISSION_LABELS: Record<PermissionLevel, string> = {
  viewer: "Viewer",
  worker: "Worker",
  manager: "Manager",
  admin: "Admin",
};

/** Map UI permission to backend access_level for create_member_invite / property_access */
export function permissionToAccessLevel(permission: PermissionLevel): "full" | "limited" {
  return permission === "admin" ? "full" : "limited";
}

const PERMISSION_CARDS: {
  id: PermissionLevel;
  title: string;
  bullets: string[];
}[] = [
  {
    id: "viewer",
    title: "Viewer",
    bullets: [
      "View schedule, checklists, inventory, submissions",
      "No edits, no assigning, no inviting",
    ],
  },
  {
    id: "worker",
    title: "Worker",
    bullets: [
      "Complete assigned tasks",
      "Submit inventory + maintenance issues",
      "No managing settings / checklists / inventory templates",
    ],
  },
  {
    id: "manager",
    title: "Manager",
    bullets: [
      "View everything + manage schedule",
      "Delegate/assign tasks",
      "Invite team members",
      "Cannot edit property setup, calendar URLs, checklist templates, inventory configuration",
    ],
  },
  {
    id: "admin",
    title: "Admin",
    bullets: [
      "Full property operations: schedule, checklists, inventory, maintenance, assignments, invites",
      "Cannot access billing/payment methods or owner account security",
    ],
  },
];

/** Allowed permission options by invite role. Team admin: Manager/Admin only; Cleaner: Viewer/Worker only. */
export function getAllowedPermissionsByInviteRole(
  inviteRole: "company_admin" | "cleaner"
): PermissionLevel[] {
  return inviteRole === "company_admin"
    ? (["manager", "admin"] as const)
    : (["viewer", "worker"] as const);
}

/** Default permission for invite role. Team admin → Manager; Cleaner → Worker. */
export function getDefaultPermissionForInviteRole(
  inviteRole: "company_admin" | "cleaner"
): PermissionLevel {
  return inviteRole === "company_admin" ? "manager" : "worker";
}

/** One-line summary for the selected permission */
export function permissionSummary(permission: PermissionLevel, mode: "team_admin" | "worker"): string {
  switch (permission) {
    case "viewer":
      return "Can only view; no edits or invites.";
    case "worker":
      return mode === "worker"
        ? "Can complete tasks and submit inventory/maintenance; cannot manage settings."
        : "Can complete tasks and submit inventory/maintenance; cannot manage settings.";
    case "manager":
      return "Can manage schedule, delegate tasks, and invite team members; cannot edit property/checklist/inventory setup.";
    case "admin":
      return "Full access to property operations; billing and account security remain owner-only.";
    default:
      return "";
  }
}

interface PermissionCardsProps {
  value: PermissionLevel;
  onChange: (next: PermissionLevel) => void;
  mode: "team_admin" | "worker";
  /** If set, only these options are shown. Value is auto-corrected to default when not in allowed. */
  allowed?: PermissionLevel[];
}

export default function PermissionCards({ value, onChange, mode, allowed }: PermissionCardsProps) {
  const effectiveAllowed = allowed ?? (PERMISSION_CARDS.map((c) => c.id) as PermissionLevel[]);
  const defaultForMode = mode === "team_admin" ? "manager" : "worker";
  const safeValue = effectiveAllowed.includes(value) ? value : defaultForMode;

  useEffect(() => {
    if (allowed?.length && !allowed.includes(value)) {
      onChange(mode === "team_admin" ? "manager" : "worker");
    }
  }, [allowed, value, mode, onChange]);

  const cardsToShow = PERMISSION_CARDS.filter((card) => effectiveAllowed.includes(card.id));

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {cardsToShow.map((card) => {
          const isSelected = safeValue === card.id;
          return (
            <button
              key={card.id}
              type="button"
              onClick={() => onChange(card.id)}
              className={cn(
                "w-full text-left p-4 rounded-lg border-2 transition-all",
                "hover:border-primary/50 hover:bg-muted/30",
                isSelected
                  ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                  : "border-border bg-card"
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm flex items-center gap-2">
                    {card.title}
                    {isSelected && (
                      <span className="shrink-0 rounded-full bg-primary text-primary-foreground p-0.5">
                        <Check className="h-3.5 w-3.5" />
                      </span>
                    )}
                  </div>
                  <ul className="mt-2 space-y-1 text-xs text-muted-foreground list-disc list-inside">
                    {card.bullets.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">
        Selected: <span className="font-medium text-foreground">{PERMISSION_LABELS[safeValue]}</span> — {permissionSummary(safeValue, mode)}
      </p>
    </div>
  );
}
